import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Eye,
  RefreshCw,
  Loader2,
  User as UserIcon,
  Monitor,
  Clock,
  Search,
} from 'lucide-react';
import { formatRomeDateTime } from '../lib/datetime';

const roleLabels = {
  super_admin: 'Super Admin',
  hotel_manager: 'Hotel Manager',
  capo_manutentore: 'Capo Manutentore',
  technician: 'Tecnico',
  housekeeping: 'Housekeeping',
  frontoffice: 'Front Office',
};

const roleBadgeClasses = {
  super_admin: 'bg-purple-100 text-purple-800 border-purple-300',
  hotel_manager: 'bg-blue-100 text-blue-800 border-blue-300',
  capo_manutentore: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  technician: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  housekeeping: 'bg-pink-100 text-pink-800 border-pink-300',
  frontoffice: 'bg-amber-100 text-amber-800 border-amber-300',
};

const formatSecondsAgo = (s) => {
  if (s < 5) return 'ora';
  if (s < 60) return `${s}s fa`;
  const m = Math.floor(s / 60);
  return `${m} min fa`;
};

const OnlineUsersPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchData = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await api.get('/presence/online');
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(true);
    const t = setInterval(() => fetchData(false), 15000); // refresh ogni 15s
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    if (!data?.users) return [];
    return data.users.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${u.name} ${u.email} ${u.assigned_hotel_names.join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, roleFilter]);

  const totalRoles = data?.by_role || {};

  return (
    <div className="space-y-6" data-testid="online-users-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Eye className="w-6 h-6 text-emerald-600" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Utenti Online
            </h1>
            <span className="relative flex h-2.5 w-2.5 ml-1">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Utenti attivi negli ultimi {data?.threshold_seconds || 90} secondi · refresh automatico ogni 15s
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchData(true)}
          disabled={loading}
          data-testid="refresh-online-users-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Aggiorna
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Totale Online</div>
            <div className="text-3xl font-bold text-emerald-900 mt-1 tabular-nums" data-testid="kpi-total-online">
              {data?.count || 0}
            </div>
          </CardContent>
        </Card>
        {Object.entries(roleLabels).map(([role, label]) => (
          <Card key={role}>
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{label}</div>
              <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums" data-testid={`kpi-role-${role}`}>
                {totalRoles[role] || 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca per nome, email o hotel..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="online-users-search"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[200px]" data-testid="online-users-role-filter">
              <SelectValue placeholder="Tutti i ruoli" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i ruoli</SelectItem>
              {Object.entries(roleLabels).map(([role, label]) => (
                <SelectItem key={role} value={role}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Users table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {filtered.length} {filtered.length === 1 ? 'utente trovato' : 'utenti trovati'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="p-12 flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento...
            </div>
          ) : error ? (
            <div className="p-12 text-center text-red-600 text-sm">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">Nessun utente online corrisponde ai filtri.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Utente</th>
                    <th className="text-left px-4 py-3 font-semibold">Ruolo</th>
                    <th className="text-left px-4 py-3 font-semibold">Hotel</th>
                    <th className="text-left px-4 py-3 font-semibold">Device</th>
                    <th className="text-left px-4 py-3 font-semibold">IP</th>
                    <th className="text-right px-4 py-3 font-semibold">Ultimo ping</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`online-user-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                              <UserIcon className="w-4 h-4 text-slate-500" />
                            </div>
                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">{u.name}</div>
                            <div className="text-xs text-slate-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={roleBadgeClasses[u.role] || ''}>
                          {roleLabels[u.role] || u.role}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {u.assigned_hotel_names.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {u.assigned_hotel_names.slice(0, 2).map((h, i) => (
                              <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{h}</span>
                            ))}
                            {u.assigned_hotel_names.length > 2 && (
                              <span className="text-xs text-slate-400">+{u.assigned_hotel_names.length - 2}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Monitor className="w-3.5 h-3.5 text-slate-400" />
                          {u.device}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono">{u.ip}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5 text-xs text-slate-600">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span className="tabular-nums" title={formatRomeDateTime(u.last_seen)}>
                            {formatSecondsAgo(u.seconds_ago)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnlineUsersPage;
